import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Store, ChefHat, Star } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ContactForm from './ContactForm';
import { reels } from '../data/instagram';
import styles from '../styles/components/Hero.module.css';
import buttonStyles from '../styles/components/Button.module.css';
import featuresStyles from '../styles/components/Features.module.css';
import farmersMarketStyles from '../styles/components/FarmersMarket.module.css';
import instagramStyles from '../styles/components/InstagramFeed.module.css';

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const id = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(id);
  }, [location.state]);

  useEffect(() => {
    if (reels.length === 0) return;
    const existing = document.getElementById('instagram-embed-script');
    if (!existing) {
      const script = document.createElement('script');
      script.id = 'instagram-embed-script';
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      document.body.appendChild(script);
    } else if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process();
    }
  }, []);

  const scrollToMarkets = (e) => {
    if (e) e.preventDefault();
    document.getElementById('farmers-markets')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="container">
      <Helmet>
        <title>Microgreens for Chefs & Everyday Meals | Folsom Microgreens</title>
        <meta
          name="description"
          content="Fresh microgreens for chefs and everyday meals. Locally grown in Folsom, harvested and delivered the same day. Available at farmers markets and for bulk orders."
        />
        <link rel="canonical" href="https://www.folsommicrogreens.com/" />
      </Helmet>

      <div className={styles.hero}>
        <div className={styles.glow}></div>
        <h1 className={styles.title}>Harvested today. Delivered today.</h1>
        <p className={styles.subtitle}>
          Microgreens cut Sunday morning and at your door the same day. Folsom and El Dorado Hills. Bulk orders delivered Thursdays.
        </p>
        <div className={styles.ctaContainer}>
          <Link to="/microgreens" className={buttonStyles.primary}>
            Shop Microgreens <ShoppingCart className={buttonStyles.icon} />
          </Link>
          <Link to="/chefs" className={buttonStyles.outline}>
            For Chefs <ChefHat className={buttonStyles.icon} />
          </Link>
        </div>
      </div>

      <p className={styles.explainer}>
        Microgreens are young vegetable greens, harvested days after sprouting for
        big flavor and crunch. <strong>Sprinkle them on eggs, sandwiches, bowls, wraps,
        and any meal you're already making.</strong>
      </p>

      {/* Audience picker — single fork */}
      <div className={featuresStyles.features}>
        <div className={featuresStyles.box}>
          <ChefHat className={featuresStyles.symbol} />
          <h3 className={featuresStyles.label}>For Chefs</h3>
          <p className={featuresStyles.description}>Clamshells or bulk by the ounce. Standing weekly orders, 10–14 day shelf life. A free clamshell or two on request for new restaurant accounts.</p>
          <Link to="/chefs" className={featuresStyles.boxCta}>
            For Chefs & Restaurants
          </Link>
        </div>
        <div className={featuresStyles.box}>
          <Star className={featuresStyles.symbol} />
          <h3 className={featuresStyles.label}>For Everyday Meals</h3>
          <p className={featuresStyles.description}>Sunday delivery in Folsom and El Dorado Hills — harvested and delivered the same day. Pick the greens you want.</p>
          <Link to="/microgreens" className={featuresStyles.boxCta}>
            Shop Microgreens
          </Link>
        </div>
        <div className={featuresStyles.box}>
          <Store className={featuresStyles.symbol} />
          <h3 className={featuresStyles.label}>Farmers Markets</h3>
          <p className={featuresStyles.description}>Visit us at local markets to sample and purchase our microgreens directly.</p>
          <a href="#farmers-markets" onClick={scrollToMarkets} className={featuresStyles.boxCta}>
            Market Schedule
          </a>
        </div>
      </div>

      <div id="farmers-markets" className={farmersMarketStyles.farmersMarket} style={{ scrollMarginTop: '5rem' }}>
        <div className={farmersMarketStyles.container}>
          <h2 className={farmersMarketStyles.title}>Available at Local Farmers Markets!</h2>
          <p className={farmersMarketStyles.subtitle}>Stop by to sample what's freshly cut this week and meet the team</p>
          <div className={farmersMarketStyles.marketInfo}>
            <div className={farmersMarketStyles.infoBox}>
              <Store className={featuresStyles.symbol} />
              <h3>Palladio Folsom Farmer's Market</h3>
              <p>Every Wednesday from 9:00 AM to 1:00 PM</p>
              <p>Located at the Wholefoods Market Parking Lot</p>
              <a href="https://www.google.com/maps/dir//260+Palladio+Pkwy,+Folsom,+CA+95630/@38.6481686,-121.200044,12z/data=!4m8!4m7!1m0!1m5!1m1!1s0x809ae5b3626d4da5:0x9039ce2ed0d6c17e!2m2!1d-121.117643!2d38.6481975?entry=ttu&g_ep=EgoyMDI1MDQwNy4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className={farmersMarketStyles.cta}>
                Get Directions
              </a>
            </div>

            <div className={farmersMarketStyles.infoBox}>
              <Store className={featuresStyles.symbol} />
              <h3>Town Center El Dorado County Certified Farmer's Market</h3>
              <p>Every Saturday from 9:00 AM to 2:00 PM</p>
              <p>4364 Town Center Blvd, El Dorado Hills, CA 95762</p>
              <a href="https://www.google.com/maps/place/4370+Town+Center+Blvd,+El+Dorado+Hills,+CA+95762/@38.6852,-121.0824,15z/data=!4m6!3m5!1s0x809b1c3c1c1c1c1c:0x1c1c1c1c1c1c1c1c!8m2!3d38.6852!4d-121.0824!16s%2Fg%2F11c1c1c1c1" target="_blank" rel="noopener noreferrer" className={farmersMarketStyles.cta}>
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </div>

      {reels.length > 0 && (
        <section className={instagramStyles.section}>
          <h2 className={instagramStyles.title}>Latest from @folsommicrogreens</h2>
          <p className={instagramStyles.subtitle}>
            Behind the grow lights — from harvest to plate.
          </p>
          <div className={instagramStyles.grid}>
            {reels.map((url) => (
              <div key={url} className={instagramStyles.gridItem}>
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink={url}
                  data-instgrm-version="14"
                />
              </div>
            ))}
          </div>
          <p className={instagramStyles.cta}>
            <a href="https://instagram.com/folsommicrogreens" target="_blank" rel="noopener noreferrer">
              See more on Instagram →
            </a>
          </p>
        </section>
      )}

      <ContactForm />
    </div>
  );
};

export default Home;
